// Angular
import { Injectable } from '@angular/core';

// ngrx
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

// RxJS
import { of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

// App
import { AppState } from '@app/store';
import * as AssetActions from '../../asset/actions/assets.actions';
import * as FiltersActions from '../actions/filters.actions';
import * as fromFilters from '../selectors/filters.selectors';
import * as SearchFiltersActions from '@app/store/filters/actions/search-filters.actions';
import { buildAssetsParams } from '@app/modules/location-client/utilities';
import { AssetsParams } from '@app/modules/location-client/location-api.models';
import { selectAllPermissions } from '@app/store/permissions/selectors/permissions.selectors';
import { FilterActionTypes } from '@app/store/filters/actions/filters.actions';
import { selectAllVehicles } from '@app/store/asset/selectors/assets.selectors';

@Injectable()
export class FilterEffects {
  constructor(private actions$: Actions, private store: Store<AppState>) {}

  loadAssetsOnUpdatedFilters$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          FiltersActions.setDefaultFilter,
          FiltersActions.applySorting,
          FiltersActions.clearFilters,
          FiltersActions.setCompany
        ),
        concatLatestFrom(() => [
          this.store.select(fromFilters.selectAllFilters),
          this.store.select(selectAllPermissions),
          this.store.select(selectAllVehicles)
        ]),
        map(([action, filters, permissions, vehiclesMap]) => {
          const vehicles = [...vehiclesMap.values()];
          const assetsParams = buildAssetsParams(<AssetsParams>{}, filters, vehicles);
          if (assetsParams?.companyId) {
            if (vehicles?.length) {
              this.store.dispatch(AssetActions.loadAssets({ assetsParams }));
              this.store.dispatch(AssetActions.loadAssetsOnUpdatedFiltersSuccess());
            } else {
              this.store.dispatch(AssetActions.loadAssetsEmptyListSuccess());
            }
          }
        })
      );
    },
    { dispatch: false }
  );

  setCompanyFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FiltersActions.setCompany),
      concatLatestFrom(() => this.store.select(fromFilters.selectCompany)),
      filter(([action, companyFilter]) => action?.company !== companyFilter?.id),
      switchMap(([action, companyFilter]) => {
        return [
          SearchFiltersActions.clearSearchFiltersDivisions(),
          FiltersActions.clearDivisions(),
          AssetActions.loadSelectedAsset(null),
          SearchFiltersActions.getSearchFiltersDivisions({ companyId: action.company })
        ];
      })
    )
  );

  // If we clear a company filter as a zonar user, we should also reset the division state.
  clearDivisionsOnClearCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FiltersActions.clearCompany),
      switchMap(_ => of(FiltersActions.clearDivisions()))
    )
  );
}
